import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { collectionsAPI, artworksAPI } from '../services/api';
import { collections as mockCollections, artworks as mockArtworks } from '../data/mock';
import { getImageUrl } from '../utils/imageUrl';
import { formatPrice, getLowestPrice } from '../utils/currency';
import { useCurrency } from '../context/CurrencyContext';
import ProtectedImage from '../components/common/ProtectedImage';
import SEO from '../components/common/SEO';

const CollectionsPage = () => {
  const { slug } = useParams();
  const [collections, setCollections] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sort collections: "Paintings" first, then alphabetically
  const sortCollections = (colls) => {
    return [...colls].sort((a, b) => {
      if (a.name === 'Paintings') return -1;
      if (b.name === 'Paintings') return 1;
      return a.name.localeCompare(b.name);
    });
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [collectionsData, artworksData] = await Promise.all([
          collectionsAPI.getAll(),
          artworksAPI.getAll()
        ]);
        const rawCollections = collectionsData.length > 0 ? collectionsData : mockCollections;
        setCollections(sortCollections(rawCollections));
        setArtworks(artworksData.length > 0 ? artworksData : mockArtworks);
      } catch (error) {
        console.error('Error loading data:', error);
        setCollections(sortCollections(mockCollections));
        setArtworks(mockArtworks);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="py-20 text-center text-gray-500">Loading...</main>
        <Footer />
      </div>
    );
  }

  // If slug is provided, show single collection
  if (slug) {
    const collection = collections.find(c => c.slug === slug);
    const collectionArtworks = artworks.filter(a => 
      a.collectionId === collection?.id || a.collection_id === collection?.id
    );

    if (!collection) {
      return (
        <div className="min-h-screen bg-white">
          <Header />
          <main className="py-20 text-center">
            <h1 className="text-2xl text-gray-600">Collection not found</h1>
            <Link to="/collections" className="mt-4 inline-block text-black underline">View all collections</Link>
          </main>
          <Footer />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-white">
        <SEO 
          url={`/collections/${slug}`}
          title={collection.name}
          description={collection.description || `Shop the ${collection.name} - Fine art prints and original paintings by Gorito.`}
          image={getImageUrl(collection.image)}
        />
        <Header />
        <main>
          {/* Collection Hero */}
          <div className="relative h-[50vh] flex items-center justify-center overflow-hidden">
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${getImageUrl(collection.image)})` }}
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative z-10 text-center text-white px-4">
              <h1 className="text-4xl md:text-5xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                {collection.name}
              </h1>
              <p className="text-lg text-white/90 max-w-xl mx-auto">
                {collection.description}
              </p>
            </div>
          </div>

          {/* Artworks Grid */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {collectionArtworks.length > 0 ? (
                collectionArtworks.map((artwork) => (
                  <ArtworkCard key={artwork.id} artwork={artwork} />
                ))
              ) : (
                // Show all artworks if no specific collection artworks
                artworks.slice(0, 8).map((artwork) => (
                  <ArtworkCard key={artwork.id} artwork={artwork} />
                ))
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show all collections
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        url="/collections"
        title="Art Collections"
        description="Explore our curated collections of fine art prints and original paintings by Swedish artist Gorito. From portraits of iconic musicians to meditative kaleidoscopes."
      />
      <Header />
      <main>
        {/* Page Header */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-light text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
              Collections
            </h1>
            <p className="mt-4 text-lg text-gray-600">
              Explore our curated collections of fine art prints and original paintings
            </p>
          </div>
        </div>

        {/* Collections Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {collections.map((collection) => (
              <CollectionCard key={collection.id} collection={collection} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const CollectionCard = ({ collection }) => {
  return (
    <Link
      to={`/collections/${collection.slug}`}
      className="group relative aspect-[4/3] overflow-hidden rounded-lg"
    >
      <img
        src={getImageUrl(collection.image)}
        alt={collection.name}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <h2 className="text-2xl font-light text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
          {collection.name}
        </h2>
        <p className="text-white/80 text-sm">
          {collection.description}
        </p>
      </div>
    </Link>
  );
};

const ArtworkCard = ({ artwork }) => {
  const { currency } = useCurrency();
  const priceType = artwork.price_type || artwork.priceType || 'from';
  const lowestPrice = getLowestPrice(artwork.sizes, artwork.price);
  const isSold = artwork.sold === true;
  
  return (
    <Link to={`/product/${artwork.slug}`} className="group">
      <div className="aspect-square overflow-hidden rounded-md bg-gray-100">
        <ProtectedImage
          src={getImageUrl(artwork.image)}
          alt={artwork.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="mt-4">
        <h3 className="text-sm text-gray-900 group-hover:underline line-clamp-2">
          {artwork.title}
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          {isSold ? (
            <span className="text-red-600 font-medium">Sold</span>
          ) : (
            <>{priceType === 'fixed' ? '' : 'From '}{formatPrice(lowestPrice, currency)}</>
          )}
        </p>
      </div>
    </Link>
  );
};

export default CollectionsPage;
